import './BdaApplicationEdit.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb, Button, Col, Form, Modal, Navbar, Row } from 'react-bootstrap';
import DesignerComponent from '../../components/designer/Designer';
import DesignerPalette from '../../components/designer/DesignerPalette';
import DesignerInspector from '../../components/designer/DesignerInspector';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { cloneDeep } from 'lodash';
import { Applications } from '../../api/Applications';
import { useToasts } from '../../contexts/toasts';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { inspectingItemTypeSelector, setInspectingItem } from '../../slices/inspectorSlice';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { changeMode, resetState, setApplication, setApplicationAccessLevel, setAvailableStorages, setDefaultStorage } from '../../slices/designerSlice';
import { GraphV2, refreshNodesCategories, translateGraph, validateApplication } from '../../utils/DesignerUtils';
import { BdaApplication, Note } from '../../models/BdaApplication';
import { Helmet } from 'react-helmet';
import AlidaLoader from '../../components/common/AlidaLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { accessLevelOptions } from '../../utils';
import { Select } from 'antd';
import { Datasources } from '../../api/Datasources';
import { RootState } from '../../store';
import { Datasource } from '../../models/Datasource';
import { Workflow, WorkflowMode } from '../../models/Workflow';
import { AccessLevel, UsedItem } from '../../models/generics';
import { BdaService } from '../../models/BdaService';
import { ReactFlowProvider, useReactFlow } from 'reactflow';
import { Authentication } from '../../api/Authentication';
import Cookies from 'js-cookie';
import { useAuth } from '../../contexts/auth';
import AnnotationsForm from '../../components/forms/AnnotationsForm';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { EventEmitter } from '../../utils/EventEmitter';
import { Categories } from '../../api/Categories';
import ServiceMarkdownModal from '../../components/generics/ServiceMarkdownModal';

function BdaApplicationEdit() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fromImport] = useState(searchParams.has('importMode') && searchParams.get('importMode'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [applicationName, setApplicationName] = useState("");

  const [initialGraph, setInitialGraph] = useState<GraphV2>();
  const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();

  const [showPalette, setShowPalette] = useState(true);
  const [showInspector, setShowInspector] = useState(true);
  const [selectedMode, setSelectedMode] = useState<WorkflowMode>();
  const [workflow, setWorkflow] = useState<Workflow>();
  const [accessLevel, setAccessLevel] = useState<AccessLevel>();
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [datasource, setDatasource] = useState<number>();
  const [showSettingsModal, setShowSettingsModal] = useState(!id);
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const [service, setService] = useState<BdaService>();
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesKey, setNotesKey] = useState<"service" | "workflow">();
  const inspectingItemType = useSelector(inspectingItemTypeSelector);
  const inspectingItem = useSelector((state: RootState) => state.inspector.inspectingItem);
  const usedItems = useSelector((state: RootState) => state.designer.usedItems);
  const availableStorages = useSelector((state: RootState) => state.designer.availableStorages);
  const defaultStorage = useSelector((state: RootState) => state.designer.defaultStorage);
  const application = useSelector((state: RootState) => state.designer.application);

  const { setToken } = useAuth();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));

  const fromTemplate = useMemo(() => searchParams.has('fromTemplate') && searchParams.get('fromTemplate') === 'true', [searchParams]);

  useEffect(() => {
    if (id) initApplication();
    else if (fromImport) importApplication();
    // else dispatch(setGraph([]));

    // designer onDestroy
    return () => {
      dispatch(setInspectingItem(null));
      dispatch(resetState())
      localStorage.removeItem('applicationToImport');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => setDatasource(undefined), [accessLevel]);

  useEffect(() => {
    if (accessLevel) {
      setDatasources(undefined);
      initDatasources();
    }
    // eslint-disable-next-line
  }, [accessLevel]);

  useEffect(() => {
    if (selectedMode) {
      dispatch(changeMode(selectedMode));
      if (!id) {
        dispatch(setApplicationAccessLevel(accessLevel!));
        dispatch(setDefaultStorage(datasource!));
      }
    }
    // eslint-disable-next-line
  }, [selectedMode]);

  const onInspectorClose = () => setShowInspector(false);
  const handleSettingsModalClose = () => setShowSettingsModal(false);
  const cancel = () => navigate("/bda-applications");

  const handleNodesRemoved = useCallback((ids: string[]) => {
    if (ids.includes(inspectingItem.ref)) cancelInspection();
  }, [inspectingItem]);

  useEffect(() => {
    EventEmitter.subscribe("designer:nodes-removed", handleNodesRemoved);
    return () => EventEmitter.unsubscribe("designer:nodes-removed");
  }, [handleNodesRemoved]);

  const cancelInspection = () => {
    // simulating a click event on react-flow pane to trigger onPaneClick handler
    const reactFlowPane = document.getElementsByClassName('react-flow__pane').item(0) as HTMLDivElement;
    reactFlowPane.click();
  }

  const initDatasources = () => {
    const params = new URLSearchParams([['accessLevel', `${accessLevel}`]]);

    Datasources.getDatasources(params)
      .then(
        ({ data }) => {
          dispatch(setAvailableStorages(data));
          setDatasources(data.filter(ds => ds.driverType === 'minio'));
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `${datasourceLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const importApplication = () => {
    const applicationDefinition = localStorage.getItem('applicationToImport');

    if (!applicationDefinition) {
      setSearchParams(undefined);
      // dispatch(setGraph([]));
      return;
    }

    try {
      const applicationObject = JSON.parse(applicationDefinition);
      const newApplication = new BdaApplication(applicationObject);

      console.log('import application', newApplication);
      dispatch(setApplication({ ...newApplication }));

      const graph = translateGraph(newApplication.workflows[0].procedure);
      setInitialGraph(graph);
      setEdges(graph.edges);
      setNodes(graph.nodes);
      setWorkflow(newApplication.workflows[0]);
    }
    catch (e) {
      console.log(`error importing application.\n`, e);
      localStorage.removeItem('applicationToImport');
      setSearchParams(undefined);
    }
  }

  const initApplication = () => {
    const params = new URLSearchParams();
    params.set("forEditing", `${true}`);

    Promise.all([Applications.getApplication(+id!, params), Categories.getCategories()])
      .then(
        ([{ data: application }, { data: categories }]) => {
          setApplicationName(application.name);
          setAccessLevel(application.accessLevel);
          dispatch(setApplication(application));
          const procedure = cloneDeep(application.workflows[0].procedure);
          const fixedGraph = procedure.map(node => {
            if (node.type === "batch" || node.type === "stream") {
              node.data.label = node.data.label || node.data.name || node.data.text;
              node.data.name = node.data.name || node.data.text;
            }
            return node;
          });
          const updatedGraph = refreshNodesCategories(fixedGraph, categories);
          const graph = translateGraph(updatedGraph);
          setInitialGraph(graph);
          setEdges(graph.edges);
          setNodes(graph.nodes);
          dispatch(setDefaultStorage(application.defaultDatasource));
          setSelectedMode(application.workflows[0].mode);
          setWorkflow(application.workflows[0]);
        },
        () => {
          showToast('get-application-error', `Could not get ${applicationLabels.singular}`, `${applicationLabels.singular} Error`, 'danger');
        }
      )
  }

  const fixApplication = (application: BdaApplication) => {
    const services: UsedItem<BdaService>[] = cloneDeep(usedItems.filter(item => item.type && item.type !== 'dataset' && item.type !== 'model'));

    services.forEach(service => {
      service.properties!.forEach(prop => {
        if (application.workflows[0].mode === "BATCH" && (prop.key.includes('output-dataset') || prop.key.includes('output-model'))) {
          const targetStorage = service.outputs!.find(out => out.id === prop.key)?.targetStorage;
          const assetKey = prop.key.includes('output-dataset') ? "dataset" : "model";
          prop[assetKey] = {
            ...prop[assetKey],
            datasource: availableStorages?.find(storage => storage.id === targetStorage) || undefined,
          }
        }

        if (prop.value === null || prop.value === undefined || prop.value === "") prop.value = prop.defaultValue;

        if (prop.valueType === "JSON" && !prop.interpolationEnabled) prop.value = JSON.stringify(JSON.parse(prop.value));

        if (prop.key.includes("input-columns")) {
          const datasetKey = prop.key.replace("columns", "dataset");
          const input = service.inputs?.find(input => input.id === datasetKey);
          if (!input) console.warn(`Expected property with key ${datasetKey} not found`);
          const columns = (input.dataset?.columns || []) as any[];
          prop.value = input.cascade ? columns : columns.filter(col => col.selected).map(col => col.name).join(",");
        }
      });

      delete service.inputs;
      delete service.outputs;
      delete service.cascade;
      delete service.type;
    });

    // setting whole asset object inside "data" field
    const fixedGraph: any[] = getNodes().map((node) => {
      const found = usedItems.find(item => item.ref === node.id);
      if (node.type === "dataset") {
        return {
          ...node,
          data: {
            ...node.data,
            dataset: found,
          }
        };
      }
      else if (node.type === "model") {
        return {
          ...node,
          data: {
            ...node.data,
            model: found,
          }
        };
      }
      else return node;
    });

    application.workflows[0].services = services as BdaService[];
    application.workflows[0].procedure = fixedGraph.concat(...getEdges());
    // TODO remove this
    if (application.workflows[0].mode === 'STREAM') application.workflows[0].targetPlatform = 'K8S';

    return application;
  }

  const handleApplicationSubmit = (application: BdaApplication) => {
    const editApplication = cloneDeep(application);
    const applicationToSubmit = fixApplication(editApplication);
    const validation = validateApplication(applicationToSubmit);

    if (!validation.valid) {
      showToast(
        'application-error',
        (
          <>
            <span>{application.template ? 'Template' : applicationLabels.singular} services require some missing properties.</span>
            <ul>
              {validation.errors.map((error, index) => (
                <li key={index}>
                  <b>{error.service}</b>
                  <ul>
                    {error.missingProperties.map(prop => (
                      <li key={prop}>{prop}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        ),
        'Missing Information', 'danger');
      return;
    }
    applicationToSubmit.defaultDatasource = applicationToSubmit.defaultDatasource || availableStorages!.find(storage => storage.id === defaultStorage) || availableStorages!.find(storage => storage.default);

    showLoader();

    const promise = fromTemplate
      ? Applications.createApplication({ ...applicationToSubmit, template: false, defaultVersion: true })
      : id
        ? Applications.updateApplication(+id!, applicationToSubmit)
        : Applications.createApplication(applicationToSubmit)

    promise
      .then(
        ({ data }) => {
          hideLoader();
          showToast(`application-${!id || fromTemplate ? "create" : "edit"}-success`, `${applicationLabels.singular} ${!id || fromTemplate ? "created" : "updated"} successfully`, `${applicationLabels.singular} Success`, 'success');
          if (!id) {
            Authentication.refreshToken()
              .then(
                ({ data: tokenInfo }) => {
                  setToken(tokenInfo.access_token);
                  Cookies.set(window.__env.AUTH_TOKEN_NAME || "alida-token", tokenInfo.access_token, { expires: tokenInfo.expires_in });
                  Cookies.set(window.__env.AUTH_REFRESH_TOKEN_NAME || "alida-refresh-token", tokenInfo.refresh_token, { expires: tokenInfo.refresh_expires_in });
                  navigate(`/bda-applications/${data.id}`);
                },
                (err) => {
                  const message = err.response?.data?.message || err.message;
                  const status = err.response?.status;
                  console.log("Error refreshing token:", status, message);
                  navigate(`/bda-applications/${data.id}`);
                  // showToast('refresh-token-error', message, `Refresh Token Error ${status ? `(${status})` : ''}`, 'danger');
                }
              )
          }
          else navigate(`/bda-applications/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast(`application-${id ? "edit" : "create"}-error`, message, `${applicationLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const togglePalette = () => setShowPalette(!showPalette);
  const toggleInspector = () => setShowInspector(!showInspector);

  const handleConfirm = () => {
    setSelectedMode(WorkflowMode.BATCH);
    handleSettingsModalClose();
    if (fromImport) setSearchParams(undefined);
  }

  const handleShowNotes = useCallback((key: "service" | "workflow") => {
    setNotesKey(key);
    setShowNotesModal(true);
  }, []);

  const handleNotesModalClose = useCallback(() => {
    setShowNotesModal(false);
    setNotesKey(undefined);
  }, []);

  const handleShowMarkdown = useCallback((service: BdaService) => {
    setService(service);
    setShowMarkdownModal(true);
  }, []);

  const handleMarkdownModalClose = useCallback(() => {
    setService(undefined);
    setShowMarkdownModal(false);
  }, []);

  const handleNotesSubmit = useCallback((key: string, notes: Note[]) => {
    handleNotesModalClose();
    if (key === "service")
      dispatch(setInspectingItem({ ...inspectingItem, notes }));
    if (key === "workflow")
      dispatch(setApplication({ ...application, notes }));
  }, [inspectingItem, handleNotesModalClose, dispatch, application]);

  const navigateToServiceProperties = useCallback((serviceId: number) => {
    navigate(`/bda-applications/${id}/services/${serviceId}`);
  }, [navigate, id]);

  return (
    <div className="wrapper">
      <Helmet>
        <title>{applicationLabels.singular} {id ? `${id} Edit` : "Design"}</title>
      </Helmet>

      <Navbar className="p-2 align-items-center border-bottom" bg="light">
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/bda-applications" }}>{applicationLabels.plural}</Breadcrumb.Item>
          {id && <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/bda-applications/${id}` }}>{applicationName}</Breadcrumb.Item>}
          <Breadcrumb.Item active>{fromTemplate ? "Create from template" : (id ? "Edit" : "Create") + ` ${applicationLabels.singular}`}</Breadcrumb.Item>
        </Breadcrumb>
      </Navbar>

      <ServiceMarkdownModal show={showMarkdownModal} onClose={handleMarkdownModalClose} service={service} />

      <Modal show={showNotesModal} size="lg" onHide={handleNotesModalClose}>
        <Modal.Header closeButton>
          <Modal.Title className="text-capitalize">Edit {notesKey || ""} Notes</Modal.Title>
        </Modal.Header>
        <AnnotationsForm notes={notesKey === "service" ? inspectingItem?.notes : application?.notes} initialPath={notesKey} onSubmit={handleNotesSubmit} onCancel={handleNotesModalClose} />
      </Modal>

      <Modal backdrop="static" keyboard={false} show={showSettingsModal}>
        <Modal.Header>
          <Modal.Title>{applicationLabels.singular} Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="py-2 mb-3 text-center">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Select privacy level and default {datasourceLabels.singular} before proceeding:</span>
          </Alert>

          <Form.Group className="d-flex align-items-center mb-3">
            <Col xs={4}>
              <Form.Label>Access Level</Form.Label>
            </Col>
            <Select
              placeholder="Select privacy level"
              value={accessLevel}
              onChange={setAccessLevel}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Form.Group>

          <Form.Group className="d-flex align-items-center">
            <Col xs={4}>
              <Form.Label>Default {datasourceLabels.singular}</Form.Label>
            </Col>
            <Select
              placeholder={`Select default ${datasourceLabels.singular}`}
              value={datasource}
              onChange={setDatasource}
              className="w-100"
              options={datasources?.map(ds => ({ label: ds.name, value: ds.id }))}
              loading={accessLevel && !datasources}
              disabled={!accessLevel}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="light" className="border" onClick={cancel}>Cancel</Button>
          <Button variant="success" disabled={!accessLevel || !datasource} onClick={handleConfirm}>Confirm</Button>
        </Modal.Footer>
      </Modal>

      {(!selectedMode || !getNodes()) && (
        <div className="d-flex justify-content-center py-5">
          <AlidaLoader size={250} />
        </div>
      )}

      {selectedMode && (
        <Row className="designer-container gx-0">
          <aside className={classNames(['palette-sidebar', { open: showPalette }])}>
            <DesignerPalette onShowServiceMarkdown={handleShowMarkdown} applicationAccessLevel={accessLevel} />
          </aside>

          <Col>
            <Button className={classNames("trigger palette-trigger", { in: !showPalette })} onClick={togglePalette} variant="light">
              <FontAwesomeIcon icon={solid('bars')} fixedWidth />
            </Button>
            <Button className={classNames("trigger inspector-trigger", { in: !showInspector })} onClick={toggleInspector} variant="light">
              <FontAwesomeIcon icon={solid('eye')} fixedWidth />
            </Button>
            {getNodes() && (
              <DesignerComponent
                showInspector={showInspector}
                showPalette={showPalette}
                onShowInspectorChange={setShowInspector}
                onShowPaletteChange={setShowPalette}
                editMode={!!fromImport || !!id}
                initialNodes={initialGraph?.nodes}
                initialEdges={initialGraph?.edges}
                workflow={workflow}
              // onExportGraph={downloadGraph}
              // onImportGraph={openImportModal}
              />
            )}
          </Col>

          <aside className={classNames(['inspector-sidebar', { open: showInspector }, { 'inspecting-dataset': inspectingItemType?.includes('input-dataset') }])}>
            <DesignerInspector
              onClose={onInspectorClose}
              onApplicationSubmit={handleApplicationSubmit}
              onInspectionCancel={cancelInspection}
              onShowServiceMarkdown={handleShowMarkdown}
              onShowNotes={handleShowNotes}
              fromTemplate={fromTemplate}
              onEditServiceProperties={id ? navigateToServiceProperties : undefined}
            />
          </aside>
        </Row>
      )}
    </div>
  );
}

export default function withProvider() {
  return (
    <ReactFlowProvider>
      <BdaApplicationEdit />
    </ReactFlowProvider>
  )
}
